import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { useNavigate } from 'react-router-dom'

import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore'

import { sortByDateKey } from '../../util/sorting'
import { theme } from '../../themes/theme'
import { SpaceBetweenRowContainer, FlexEndContainer, WhiteBGContainer } from '../styles/Containers'
import { TableHeaderButton } from '../common/Button'
import BodyText from '../styles/Text'
import PIR from './PIR'
import pirTerminology from '../../util/pirTerminology'

const Headings = styled(SpaceBetweenRowContainer)`
  padding: 0px 30px;
  align-self: stretch;
`

const Item = styled.div`
  flex-basis: ${props => (props.size === 'small' ? '27%' : '38%')};
  font-size: 16px;
`

const SmallItem = styled.div`
  flex-basis: ${props => (props.size === 'small' ? '19%' : '24%')};
`

const LastItem = styled(FlexEndContainer)`
  min-width: 115px;
`

const Table = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`

const Empty = styled(BodyText)`
  display: flex;
  justify-content: center;
  padding-top: 15px;
`

const StyledPIR = styled(WhiteBGContainer)`
  padding: 10px 30px;

  :hover {
    background: ${theme.colors.primaryLight};
    cursor: pointer;
  }
`

const HeaderButton = styled(TableHeaderButton)`
  font-size: 16px;
  font-weight: 400;
`

const statusOrder = ['ready', 'signing', 'paid', 'completed', 'rejected', 'expired']
const statusIndex = statusOrder.reduce((r, k, v) => Object.assign(r, { [k]: v }), {})

function sortByStatus(a, b) {
  return statusIndex[a.status.toLowerCase()] - statusIndex[b.status.toLowerCase()]
}

function PIRTable({ pirList, displayRequestor = true }) {
  const [sortBy, setSortBy] = useState('status')
  const [sortedPirs, setSortedPirs] = useState()
  const [sortAscending, setSortAscending] = useState(true)

  const navigate = useNavigate()

  function sort(newSortBy) {
    if (newSortBy === sortBy) {
      setSortedPirs([...sortedPirs.reverse()])
      setSortAscending(!sortAscending)
    } else {
      setSortBy(newSortBy)
      setSortAscending(true)

      if (newSortBy === 'date') setSortedPirs(sortedPirs => sortedPirs.sort(sortByDateKey('lastUpdated')))
      else if (newSortBy === 'status') setSortedPirs(sortedPirs => sortedPirs.sort(sortByStatus))
    }
  }

  useEffect(() => {
    let sortedPirList = pirList

    if (sortBy === 'status') sortedPirList = pirList.sort(sortByStatus)
    if (sortBy === 'date') sortedPirList = pirList.sort(sortByDateKey('lastUpdated'))
    if (!sortAscending) sortedPirList = [...sortedPirList.reverse()]

    setSortedPirs(sortedPirList)
  }, [pirList])

  return (
    <Table>
      <Headings>
        <Item size={displayRequestor ? 'small' : 'large'}>{pirTerminology.getRequestIdLabel()}</Item>
        <Item size={displayRequestor ? 'small' : 'large'}>Address</Item>
        {displayRequestor && <Item size="small">Requestor Name</Item>}
        <SmallItem size={displayRequestor ? 'small' : 'large'}>
          <HeaderButton variant="text" onClick={() => sort('date')} active={(sortBy === 'date').toString()}>
            Last Status Update <UnfoldMoreIcon />
          </HeaderButton>
        </SmallItem>
        <LastItem>
          <HeaderButton variant="text" onClick={() => sort('status')} active={(sortBy === 'status').toString()}>
            Status <UnfoldMoreIcon />
          </HeaderButton>
        </LastItem>
      </Headings>

      {sortedPirs && sortedPirs.length > 0 ? (
        sortedPirs.map((pir, i) => (
          <StyledPIR key={i} data-testid="table-row" onClick={() => navigate(`/pir/${pir.id}`)}>
            <PIR pir={pir} displayRequestor={displayRequestor} />
          </StyledPIR>
        ))
      ) : (
        <Empty>No {pirTerminology.getRequestPluralName()} available</Empty>
      )}
    </Table>
  )
}

PIRTable.propTypes = {
  pirList: PropTypes.array.isRequired,
  displayRequestor: PropTypes.bool
}

export default PIRTable
